import type { FilterValues } from "features/datagrid-configs/types";
import { videoProcessingStatusFilterValues } from "../metadata/apiStatusOptions";
import { VideoMetadataFilterField, VideoProviderMetadataFilterField } from "../metadata/types";
import {
	VideoUploadsFilterField,
	VideoUploadsSourceFilterValue,
} from "./videoUploadsFilterConstants";

type VideoUploadApiFilter = {
	field: string;
	type: string;
	value: string | number | { start?: string; end?: string } | string[];
};

function pushDateRangeFilter(
	apiFilters: VideoUploadApiFilter[],
	field: string,
	range: unknown,
): void {
	if (range == null || typeof range !== "object") {
		return;
	}

	const dateRange = range as { start?: string; end?: string };
	if (dateRange.start || dateRange.end) {
		apiFilters.push({
			field,
			type: "datetimerange",
			value: {
				start: dateRange.start || undefined,
				end: dateRange.end || undefined,
			},
		});
	}
}

function pushStringArrayFilter(apiFilters: VideoUploadApiFilter[], field: string, raw: unknown): void {
	if (Array.isArray(raw) === false || raw.length === 0) {
		return;
	}

	apiFilters.push({
		field,
		type: "stringArray",
		value: raw.map((value) => String(value)),
	});
}

function pushSourceFilters(apiFilters: VideoUploadApiFilter[], source: unknown): void {
	switch (source) {
		case VideoUploadsSourceFilterValue.Manual:
			apiFilters.push({ field: "origin", type: "string", value: "manual" });
			break;
		case VideoUploadsSourceFilterValue.ApiDieLigen:
			apiFilters.push({ field: "origin", type: "string", value: "api" });
			apiFilters.push({ field: "provider", type: "string", value: "dieligen" });
			break;
		case VideoUploadsSourceFilterValue.ApiVeo:
			apiFilters.push({ field: "origin", type: "string", value: "api" });
			apiFilters.push({ field: "provider", type: "string", value: "veo" });
			break;
		case VideoUploadsSourceFilterValue.Discover:
			apiFilters.push({ field: "origin", type: "string", value: "discover" });
			break;
		default:
			break;
	}
}

function pushGameIdFilter(apiFilters: VideoUploadApiFilter[], raw: unknown): void {
	if (typeof raw === "string" && raw !== "") {
		apiFilters.push({ field: VideoMetadataFilterField.GameId, type: "string", value: raw });
		return;
	}

	if (Array.isArray(raw) === false || raw.length === 0) {
		return;
	}

	const gameId = String(raw[0]);
	if (gameId !== "") {
		apiFilters.push({ field: VideoMetadataFilterField.GameId, type: "string", value: gameId });
	}
}

function pushApiStatusFilter(apiFilters: VideoUploadApiFilter[], raw: unknown): void {
	if (raw === undefined || raw === null || raw === "") {
		return;
	}

	const value = String(raw);
	const field = videoProcessingStatusFilterValues.has(value) ? "status" : "apiStatus";

	apiFilters.push({ field, type: "string", value });
}

export function buildVideoUploadApiFilters(filterValues: FilterValues): VideoUploadApiFilter[] {
	const apiFilters: VideoUploadApiFilter[] = [];

	pushSourceFilters(apiFilters, filterValues[VideoUploadsFilterField.Source]);

	const veoClubhouse = filterValues[VideoUploadsFilterField.VeoClubhouse];
	if (typeof veoClubhouse === "string" && veoClubhouse !== "") {
		apiFilters.push({
			field: VideoProviderMetadataFilterField.ClubhouseName,
			type: "string",
			value: veoClubhouse,
		});
	}

	pushDateRangeFilter(apiFilters, "createdAt", filterValues[VideoUploadsFilterField.CreatedAt]);
	pushStringArrayFilter(apiFilters, "uploadedBy", filterValues[VideoUploadsFilterField.UploadedBy]);
	pushStringArrayFilter(apiFilters, "metadataAddedBy", filterValues[VideoUploadsFilterField.SavedBy]);
	pushApiStatusFilter(apiFilters, filterValues[VideoUploadsFilterField.ApiStatus]);
	pushGameIdFilter(apiFilters, filterValues[VideoUploadsFilterField.GameId]);

	return apiFilters;
}
